/* TODO: This service has become quite large and is responsible for a lot of logic. Consider refactoring into multiple smaller services in the future. */
/* TODO: take the openweather integration out of the roster service and move it to a separate service, e.g. WeatherService, to adhere to single responsibility principle and separation of concerns. */
/* TODO: MOVE GENERAL BUSINESS LOGIC IN THE PARENT RosterService TO THE SoftlineRosterService, e.g. the logic for determining event types based on filters, so that the parent service is more reusable and less coupled to the specific implementation. */
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, @typescript-eslint/no-non-null-assertion */

import { toLocalDateString } from '../helper/toLocalDateString';

import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  linkedSignal,
  resource,
  ResourceStatus,
  ResourceStreamItem,
  signal,
  Signal,
  TemplateRef,
} from '@angular/core';
import {
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDeleteEvent,
  MbscEventDragEvent,
  MbscResource,
  MbscTimelineColumnWidth,
} from '@mobiscroll/angular';
import {
  CalendarViewType,
  Department,
  DepartmentHead,
  DetailEmployee,
  Employee,
  fillEntryTimes,
  LoadRosterDetailParams,
  PmsCalendarResource,
  ROSTER_REQUEST_CONCURRENCY,
  RosterContent,
  RosterCreateTriggerService,
  RosterEventRepository,
  RosterResourceRepository,
  RosterService,
  RosterStructureRepository,
  runPooled,
  Structure,
  toLocalDateTimeString,
} from '@softapps/pms/roster';
import {
  handleRequestError,
  MenuItem as LegacyMenuItem,
  title,
} from '@softline/application';
import { authorizationFn, UserInfoService } from '@softline/auth';
import { DateTimeTzService, HttpDataAccess, toIsoDate } from '@softline/core';
import {
  askDeleteFn,
  MessageBarService,
  modalFn,
  ModalService,
  translator,
} from '@softline/ui-core';
import { EventDetails } from '@softapps/core/events';
import { EventFormComponent } from '../components/event-form/event-form.component';
import { ExternalEventModalComponent } from '../components/external-event-modal/external-event-modal.component';
import { parseIsoDurationToHours } from '../helper/parseIsoDurationToHours';
import { SOFTLINE_TOKEN_ROSTER_EVENT_PROVIDER } from '../types/roster-event-provider';
import { getEventType } from '../helper/getEventType';
import { EventTypeEnum } from '../types/event-type-enum';
import { ResourceFilterService } from './resource-filter.service';
import { SoftlineTicketService } from './softline-ticket.service';

export class SoftlineRosterService extends RosterService<RosterContent> {
  private readonly t = translator();
  private readonly messageBarService = inject(MessageBarService);
  private readonly modalService = inject(ModalService);
  private readonly httpDataAccess = inject(HttpDataAccess);
  private readonly injector = inject(Injector);
  private readonly structureRepository = inject<RosterStructureRepository<Structure>>(
    RosterStructureRepository
  );
  private readonly eventRepository: RosterEventRepository<RosterContent> = inject(
    RosterEventRepository<RosterContent>
  );
  private readonly resourceRepository: RosterResourceRepository<
    PmsCalendarResource,
    any
  > = inject(RosterResourceRepository<PmsCalendarResource, any>);
  private readonly dateTimeTzService = inject(DateTimeTzService);
  private readonly resourceFilterService = inject(ResourceFilterService);
  private readonly ticketService = inject(SoftlineTicketService);

  private readonly modal = modalFn();
  private readonly askDelete = askDeleteFn();
  private readonly createTrigger = inject(RosterCreateTriggerService);
  private readonly userInfo = inject(UserInfoService);
  private readonly isAuthorized = authorizationFn();

  private readonly map = new Map<number | string, DetailEmployee>();

  private readonly title = title('Dienstplaner');

  private readonly userId = computed(() => {
    return this.userInfo.value()?.employeeId;
  });

  FILTER_MAP: Record<string, EventTypeEnum> = {
    tickets: EventTypeEnum.TICKET,
    appointments: EventTypeEnum.APPOINTMENT,
    holidays: EventTypeEnum.ABSENCE,
  };

  //**** SIGNALS ****/
  private readonly beginDate = signal<Signal<string>>(
    signal(this.dateTimeTzService.now())
  );
  private readonly calendarViewType = signal<Signal<CalendarViewType>>(
    signal('week' as CalendarViewType)
  );
  private readonly planMonthFrom = signal<Signal<Date>>(signal(new Date()));
  private readonly planMonthTo = signal<Signal<Date>>(signal(new Date()));
  private readonly calendarColumnWidth = signal<Signal<MbscTimelineColumnWidth>>(
    signal('xxlarge' as MbscTimelineColumnWidth)
  );

  override readonly calendarDetails = signal<Signal<'compact' | 'all'>>(
    signal('compact')
  );

  readonly selectedEventTypeFilter = signal<Signal<string[]>>(signal([]));

  readonly resourceFilterString = signal<Signal<string>>(signal(''));

  public readonly selectedDepartmentHead = signal<Signal<DepartmentHead | null>>(
    signal(null)
  );
  resourcesAvailability = resource({
    params: () => ({
      departmentIds:
        this.selectedDepartmentHead()()?.managedDepartments?.map((d) => d.id) ?? [],
      today: this.dateTimeTzService.now().split('T')[0],
    }),
    loader: async ({ params }) => {
      if (!params.departmentIds.length) return undefined;
      const requests = params.departmentIds.map((id) =>
        this.httpDataAccess.get<{ result: any[] }>({
          url: `/api/v1/statusabfrage/personaluebersicht/${params.today}/entities?idabteilung=${id}`,
        })
      );
      const responses = await Promise.all(requests);
      const reduced = responses.reduce(
        (acc, curr) => {
          curr.result.forEach((item) => {
            acc[item.id] = item.status === 'A';
          });
          return acc;
        },
        {} as Record<number, boolean>
      );
      return reduced;
    },
  });

  readonly selectedEventIds = computed(() => Array.from(this.#selectedEventIds()));
  readonly #selectedEventIds = signal<Set<string | number>>(new Set());
  override readonly selectedEvents = signal<RosterContent[]>([]);
  readonly #selectionAnchorId = signal<string | number | null>(null);
  public override readonly employeeDetail = signal<Map<
    number | string,
    DetailEmployee
  > | null>(null);

  //**** RESOURCES ****/
  private readonly structureResource = resource({
    params: () => {
      const p = this.params();
      if (!p) return undefined;
      // Primitive string key so resource compares by value, not object reference
      return `${p.pathParams.employeeId}_${toLocalDateString(p.queryParams.begindate)}_${toLocalDateString(p.queryParams.enddate)}`;
    },
    loader: async ({ params: key }) => {
      if (!key) return Promise.resolve(undefined);
      const p = this.params();
      if (!p) return Promise.resolve(undefined);
      try {
        return await this.structureRepository.load({
          employeeId: p.pathParams.employeeId,
          begindate: toLocalDateString(p.queryParams.begindate),
          enddate: toLocalDateString(p.queryParams.enddate),
        });
      } catch {
        return Promise.resolve(undefined);
      }
    },
  });

  // Stable child IDs: only notifies when the actual employee list changes
  private readonly stableChildIds = computed(() => {
    const structure = this.cachedStructure();
    if (!structure || structure.departements.length < 1) return '';
    return structure.departements
      .flatMap((dep) => dep.employees.map((emp) => emp.id))
      .filter(Boolean)
      .join(',');
  });

  private lastEventsKey: string | undefined;
  private lastEventsMap: Map<string | number, RosterContent> | undefined;

  // Events created locally (paste/duplicate) that should survive a silentRefresh
  // until the server response catches up. Maps id → created-at timestamp (ms).
  private readonly pendingLocalCreates = new Map<string | number, number>();
  private static readonly PENDING_LOCAL_CREATE_TTL_MS = 60_000;

  private readonly refreshTrigger = signal(0);
  private readonly destroyRef = inject(DestroyRef);

  private readonly eventProviders =
    inject(SOFTLINE_TOKEN_ROSTER_EVENT_PROVIDER, { optional: true }) ?? [];

  /** Employee row ids whose external events are currently being loaded. */
  readonly externalEventsLoadingIds = signal<ReadonlySet<number | string>>(new Set());

  private readonly externalEventEmployees = computed(() => {
    const structure = this.cachedStructure();
    if (!structure) return [];
    return structure.departements
      .flatMap((dep) => dep.employees)
      .filter((employee) => !!employee.upn)
      .map((employee) => ({ id: employee.id, upn: employee.upn as string }));
  });

  // Streams external events (registered RosterEventProviders) into the calendar
  // per employee as they arrive; requests run in a pool of 4.
  private readonly externalEventsResource = resource({
    params: () => {
      if (this.eventProviders.length === 0) return undefined;

      const employees = this.externalEventEmployees();
      if (employees.length === 0) return undefined;

      const start = this.startOfPeriod();
      const end = this.endOfPeriod();
      if (!start || !end) return undefined;

      const employeeKeys = employees.map((e) => `${e.id}:${e.upn}`).join(',');
      return `${start.toISOString()}_${end.toISOString()}_${employeeKeys}_${this.refreshTrigger()}`;
    },
    stream: async ({ abortSignal }) => {
      const out = signal<ResourceStreamItem<Map<string | number, RosterContent>>>({
        value: new Map(),
      });

      const employees = this.externalEventEmployees();
      const from = this.startOfPeriod().toISOString();
      const to = this.endOfPeriod().toISOString();

      const accumulated = new Map<string | number, RosterContent>();
      const pendingPerEmployee = new Map<number | string, number>(
        employees.map((e) => [e.id, this.eventProviders.length])
      );
      this.externalEventsLoadingIds.set(new Set(pendingPerEmployee.keys()));

      const finishEmployee = (id: number | string) => {
        const left = (pendingPerEmployee.get(id) ?? 1) - 1;
        pendingPerEmployee.set(id, left);
        if (left > 0) return;
        this.externalEventsLoadingIds.update((ids) => {
          const next = new Set(ids);
          next.delete(id);
          return next;
        });
      };

      const tasks = employees.flatMap((employee) =>
        this.eventProviders.map((provider) => async () => {
          try {
            const events = await provider.loadEvents({
              from,
              to,
              employee,
              abortSignal,
            });
            if (abortSignal.aborted) return;
            for (const event of events) {
              if (event.id === undefined) continue;
              accumulated.set(event.id, event);
            }
            out.set({ value: new Map(accumulated) });
          } catch (e) {
            if (!abortSignal.aborted) {
              handleRequestError(e, this.injector, { show: true });
            }
          } finally {
            finishEmployee(employee.id);
          }
        })
      );

      // Not awaited: results stream via `out` while the pool drains.
      void runPooled(tasks, 4, abortSignal).finally(() => {
        if (abortSignal.aborted) this.externalEventsLoadingIds.set(new Set());
      });

      return out;
    },
  });

  // External event count per employee row; rows without upn (skipped) have no entry.
  readonly externalEventCounts = computed<ReadonlyMap<number | string, number>>(() => {
    const counts = new Map<number | string, number>();
    if (this.eventProviders.length === 0) return counts;
    for (const employee of this.externalEventEmployees()) {
      counts.set(employee.id, 0);
    }
    for (const event of this.externalEventsResource.value()?.values() ?? []) {
      const resourceId = event.resource;
      if (typeof resourceId !== 'number' && typeof resourceId !== 'string') continue;
      counts.set(resourceId, (counts.get(resourceId) ?? 0) + 1);
    }
    return counts;
  });

  private readonly eventsResource = resource({
    params: () => {
      const childIds = this.stableChildIds();
      if (!childIds) return undefined;

      const departmentHead = this.selectedDepartmentHead()();
      if (!departmentHead) return undefined;

      const start = this.startOfPeriod();
      const end = this.endOfPeriod();
      if (!start || !end) return undefined;

      const refresh = this.refreshTrigger();
      return `${departmentHead.employee.id}_${toLocalDateString(start)}_${toLocalDateString(end)}_${childIds}_${refresh}`;
    },
    loader: async ({ params: key, abortSignal }) => {
      if (!key) return undefined;
      if (key === this.lastEventsKey && this.lastEventsMap) return this.lastEventsMap;

      const departmentHead = this.selectedDepartmentHead()();
      if (!departmentHead) return undefined;
      const departmentHeadId = departmentHead.employee.id;
      const beginDate = toLocalDateString(this.startOfPeriod());
      const endDate = toLocalDateString(this.endOfPeriod());
      const childIds = this.stableChildIds().split(',');

      const allEventsMap = new Map<string | number, RosterContent>();

      await runPooled(
        childIds.map((childId) => async () => {
          try {
            const events = await this.eventRepository.loadEvents(
              departmentHeadId,
              beginDate,
              endDate,
              Number(childId),
              abortSignal
            );
            for (const event of events ?? []) {
              if (event.id) allEventsMap.set(event.id, { ...event, resize: true });
            }
          } catch (e) {
            if (!abortSignal.aborted) handleRequestError(e, this.injector, { show: true });
          }
        }),
        ROSTER_REQUEST_CONCURRENCY,
        abortSignal
      );

      if (abortSignal.aborted) return undefined;
      this.lastEventsKey = key;
      this.lastEventsMap = allEventsMap;
      return allEventsMap;
    },
  });

  public readonly events = computed(() => {
    const rawValues = this.eventsResource.value();
    const extraEvents = this.externalEventsResource.value();

    if (!rawValues && !extraEvents?.size) return [];

    const activeFilters = this.selectedEventTypeFilter()();

    // Create a set of allowed Enums based on user selection
    const allowedTypes = new Set<EventTypeEnum>();

    for (const f of activeFilters) {
      const mappedType = this.FILTER_MAP[f];
      if (mappedType) {
        allowedTypes.add(mappedType);
        // HARD FIX: If tickets are selected, also allow PLAN types
        if (mappedType === EventTypeEnum.TICKET) {
          allowedTypes.add(EventTypeEnum.PLAN);
          allowedTypes.add(EventTypeEnum.OBJECT);
          allowedTypes.add(EventTypeEnum.TASK);
        }
      }
    }

    const result: RosterContent[] = [];

    for (const event of rawValues?.values() ?? []) {
      if (!event) continue;
      if (allowedTypes.has(getEventType(event))) {
        result.push(event);
      }
    }

    // External events classify as APPOINTMENT (stamped additionalData.type),
    // so the appointments filter chip controls them too.
    for (const event of extraEvents?.values() ?? []) {
      if (!event) continue;
      if (allowedTypes.has(getEventType(event))) {
        result.push(event);
      }
    }

    return result;
  });

  //**** COMPUTED ****/
  public override readonly menuItems = computed<LegacyMenuItem[]>(() => [
    {
      name: 'garage-roster',
      title: 'Dienstplaner',
      icon: 'fa-regular fa-calendars',
      type: 'route',
      outlet: 'nav',
      routerLink: ['/roster/softline-ticket-roster'],
    },
  ]);

  private readonly params = computed(() => {
    const beginDate = this.startOfPeriod();
    const endDate = this.endOfPeriod();
    const departmentHead = this.selectedDepartmentHead()();
    if (!departmentHead || !beginDate) return undefined;
    return {
      pathParams: {
        employeeId: departmentHead.employee.id,
      },
      queryParams: {
        begindate: beginDate,
        enddate: endDate,
      },
    };
  });
  public override readonly startOfPeriod = computed<Date>(() => {
    const dateStr = this.beginDate()();
    const calendarViewType = this.calendarViewType()();
    if (!dateStr || !calendarViewType) return new Date();
    const date = new Date(dateStr);
    switch (calendarViewType) {
      case 'day':
        return date;
      case 'week':
      case 'workWeek': {
        const currentDay = date.getDay();
        const daysToStartOfWeek = currentDay === 0 ? 6 : currentDay - 1;
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - daysToStartOfWeek);
        return startOfWeek;
      }
      case 'month': {
        const startOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        return startOfMonth;
      }
      case 'planMonth':
        return this.planMonthFrom()();
      default:
        return date;
    }
  });

  public override readonly endOfPeriod = computed<Date>(() => {
    const dateStr = this.beginDate()();
    const calendarViewType = this.calendarViewType()();
    if (!dateStr || !calendarViewType) return new Date();
    const date = new Date(dateStr);
    switch (calendarViewType) {
      case 'day':
        return new Date(date.setHours(23, 59, 59));
      case 'week':
      case 'workWeek': {
        const currentDay = date.getDay();
        const daysToEndOfWeek = currentDay === 0 ? 0 : 7 - currentDay;
        const endOfWeek = new Date(date);
        endOfWeek.setDate(date.getDate() + daysToEndOfWeek);
        return endOfWeek;
      }
      case 'month': {
        const endOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return endOfMonth;
      }
      case 'planMonth':
        return this.planMonthTo()();
      default:
        return date;
    }
  });

  public readonly cachedStructure = linkedSignal<
    { status: ResourceStatus; value: Structure | undefined },
    Structure | undefined
  >({
    source: () => {
      return {
        status: this.structureResource.status(),
        value: this.structureResource.value(),
      };
    },
    computation: (source, previous) => {
      if (source.status === 'loading' || source.status === 'reloading')
        return previous?.value ?? source.value;

      return source?.value;
    },
  });

  override readonly calendarContentLoading = computed(() => {
    return this.structureResource.isLoading() || this.eventsResource.isLoading();
  });

  // All employees from the structure, independent of the search filter.
  // Used to drive allResources in ResourceFilterService so ticket loading
  // is never triggered by a search string change.
  private readonly allEmployeesFromStructure = computed(() => {
    const structure = this.cachedStructure();
    if (!structure) return [];
    return structure.departements.flatMap((d) =>
      d.employees.map((e) => ({
        id: e.id,
        name: e.name,
        key: e.key,
        eventCreation: true,
      }))
    );
  });

  public override readonly resources: Signal<MbscResource[]> = computed(() => {
    // this is used for preventing strong flickers when changing date in view
    const structure = this.cachedStructure();
    const availability = this.resourcesAvailability.value();
    const selfFirstPermission = this.isAuthorized('ROSTER_SELF_FIRST')();

    const resourceFilterStr = this.resourceFilterString()();

    if (!structure) return [];

    const userId = this.userId();

    let departments = structure.departements // TYPO FROM BACKEND, DO NOT CHANGE
      .map((department) => {
        let employees = department.employees
          .map((employee) => {
            return {
              id: employee.id,
              name: employee.name,
              key: employee.key,
              eventCreation: true,
              availability: availability ? availability[employee.id] : undefined,
            };
          })
          .filter((e) => {
            const filterString = resourceFilterStr;
            return (
              !filterString || e.name.toLowerCase().includes(filterString.toLowerCase())
            );
          });
        // Only apply the sort if the user has permission
        if (selfFirstPermission) {
          employees = employees.sort((a, b) =>
            a.id === userId ? -1 : b.id === userId ? 1 : 0
          );
        }

        return {
          id: department.department.id,
          name: department.department.name,
          key: department.department.key,
          eventCreation: false,
          children: employees,
        };
      })
      .filter((d) => !resourceFilterStr || d.children.length > 0);

    // Move the department containing the current user to the top
    if (selfFirstPermission && userId) {
      departments = departments.sort((a, b) => {
        const aHasUser = a.children.some((c) => c.id === userId);
        const bHasUser = b.children.some((c) => c.id === userId);
        return aHasUser === bHasUser ? 0 : aHasUser ? -1 : 1;
      });
    }

    return departments;
  });

  private lastIds = '';

  constructor() {
    super();

    effect(() => {
      const date = this.createTrigger.pendingDate();
      const resources = this.resources();
      if (!date || resources.length === 0) return;
      this.createTrigger.pendingDate.set(null);
      this.createEvent([], new Date(date));
    });

    effect(() => {
      const departmentHead = this.selectedDepartmentHead()();
      if (!departmentHead) return;
      this.resourceFilterService.selectedCostCenterIds.set(
        departmentHead.managedDepartments
          .map((d) => d.costCenters.map((c) => String(c.id)).join(','))
          .join(',')
      );
    });

    effect(() => {
      const allEmployees = this.allEmployeesFromStructure();
      if (allEmployees.length === 0) return;

      const ids = allEmployees.map((r) => r.id).join(',');

      if (ids === this.lastIds) return;
      this.lastIds = ids;

      this.resourceFilterService.allResources.set(allEmployees);
    });

    const refreshInterval = setInterval(() => this.silentRefresh(), 30_000);
    this.destroyRef.onDestroy(() => clearInterval(refreshInterval));
  }

  override refreshEvents(): void {
    this.lastEventsKey = undefined;
    this.lastEventsMap = undefined;
    this.refreshTrigger.update((v) => v + 1);
  }

  // TODO: is a bit buggy cuz it doesn't wait for the refresh to finish before returning, so the caller might not see the latest events yet. Consider making this return a Promise that resolves when the refresh is done.
  private async silentRefresh(): Promise<void> {
    const departmentHead = this.selectedDepartmentHead()();
    if (!departmentHead) return;

    const childIdsStr = this.stableChildIds();
    if (!childIdsStr) return;

    const departmentHeadId = departmentHead.employee.id;
    const beginDate = toLocalDateString(this.startOfPeriod());
    const endDate = toLocalDateString(this.endOfPeriod());
    const childIds = childIdsStr.split(',');

    const allEventsMap = new Map<string | number, RosterContent>();

    try {
      await Promise.all(
        childIds.map(async (childId) => {
          try {
            const events = await this.eventRepository.loadEvents(
              departmentHeadId,
              beginDate,
              endDate,
              Number(childId)
            );
            if (!events || events.length < 1) return;
            for (const event of events) {
              if (!event.id) continue;
              allEventsMap.set(event.id, { ...event, resize: true });
            }
          } catch {
            // Silently ignore errors during background refresh
          }
        })
      );
      if (this.pendingLocalCreates.size > 0) {
        const now = Date.now();
        const currentMap = this.eventsResource.value();
        for (const [id, ts] of this.pendingLocalCreates) {
          if (allEventsMap.has(id)) {
            this.pendingLocalCreates.delete(id);
            continue;
          }
          if (now - ts > SoftlineRosterService.PENDING_LOCAL_CREATE_TTL_MS) {
            this.pendingLocalCreates.delete(id);
            continue;
          }
          const local = currentMap?.get(id);
          if (local) allEventsMap.set(id, local);
        }
      }
      this.eventsResource.set(allEventsMap);
      this.lastEventsMap = allEventsMap;
    } catch {
      // Silently ignore errors during background refresh
    }
  }

  private updateEventsMap(
    updater: (map: Map<string | number, RosterContent>) => void
  ): void {
    const eventMap = this.eventsResource.value();
    if (!eventMap) return;
    updater(eventMap);
    const newMap = new Map(eventMap);
    this.eventsResource.set(newMap);
    this.lastEventsMap = newMap;
  }

  public readonly structure = computed(() => this.structureResource.value());

  //**** CALENDAR EVENTS ****/
  override async onEventCreate(e: MbscEventCreateEvent, inst: any): Promise<void> {
    if (e.action === 'externalDrop') {
      const start = e.event.start as Date;
      let type: EventTypeEnum;
      if (e.event && e.event && typeof e.event['shortName'] !== 'undefined') {
        type = EventTypeEnum.OBJECT;
      } else {
        type = EventTypeEnum.TICKET;
      }

      const employeeId = e.event.resource as number | string;
      const datePart = toLocalDateTimeString(start).split('T')[0];
      const workschema = await this.resourceRepository
        .loadWorkschema({ date: datePart, employeeId: Number(employeeId) })
        .catch(() => null);
      const times = fillEntryTimes({
        day: start,
        employeeId,
        workschema,
        hours:
          type === EventTypeEnum.TICKET ? parseIsoDurationToHours(e.event['plannedHours']) : 0,
        events: Array.from(this.eventsResource.value()?.values() ?? []).filter(
          (event): event is RosterContent => !!event
        ),
        fallback: { from: '07:30', till: '17:00' },
        pickedStart: this.calendarViewType()() === 'day' ? start : null,
      });
      const startString = toLocalDateTimeString(times.start);
      const endString = toLocalDateTimeString(times.end);

      const structure = this.structureResource.value();
      let department: Department | null = null;

      structure?.departements.forEach((d) => {
        if (d.employees.findIndex((emp) => emp.id === e.event.resource) >= 0) {
          department = d.department;
        }
      });
      let additinaldetail = [{}];

      additinaldetail = [
        {
          tabname: type === EventTypeEnum.OBJECT ? 'OBJECT' : 'TICKET',
          tabid: e.event['id'],
          name: null,
          title: null,
          comment: null,
          key: null,
          id: null,
          state: 'UNDEFINED',
        },
      ];

      const body = [
        {
          resource: [e.event.resource],
          date: datePart,
          allDay: e.event.allDay,
          start: startString,
          end: endString,
          title: '',
          text: '',
          additionalData: {
            state: 'UNDEFINED',
            pauseHours: '00:30:00',
            plannedHours: '23:59:59',
            targetHours: '23:59:59',
            employees: [
              {
                employee: {
                  id: e.event.resource,
                  key: null,
                  name: null,
                },
                department: department,
              },
            ],
            begintime: startString,
            endtime: endString,
            type: 'PLAN',
            department: department,
            additionaldetail: additinaldetail,
          },
        },
      ] as unknown as RosterContent;
      try {
        const result = await this.eventRepository.createEvent(body);
        if (!result.id) return;
        this.updateEventsMap((map) => map.set(result.id!, { ...result, resize: true }));
        this.messageBarService.success(`Eintrag wurde geplant`);
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        this.refreshEvents();
      }
    }
  }

  override async onEventClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    // Only a real left-click dismisses an open context menu. A right-click also
    // surfaces here (as the click trailing the contextmenu gesture); closing on
    // it would shut the menu the instant it opens ("must hold right-click").
    if ((args.domEvent as MouseEvent).button === 0) {
      this.closeContextMenuOverlay?.();
    }

    const event = args.event as RosterContent;
    if (event.id === undefined) return;
    if (event['external']) return;

    const ctrlOrMeta = args.domEvent.ctrlKey || args.domEvent.metaKey;
    const shift = args.domEvent.shiftKey;

    if (shift && !ctrlOrMeta) {
      args.domEvent.preventDefault();
      args.domEvent.stopPropagation();
      this.rangeSelectFromAnchor(event);
      return;
    }

    if (ctrlOrMeta) {
      this.toggleSelectedEvents(event);
      this.#selectionAnchorId.set(event.id);
      return;
    }

    const selection = this.#selectedEventIds();

    if ((args?.domEvent as MouseEvent)?.button === 2) {
      args.domEvent.preventDefault();
      args.domEvent.stopPropagation();
      return;
    }

    args.domEvent.preventDefault();
    args.domEvent.stopPropagation();

    if (!selection.has(event.id) || selection.size > 1) {
      this.#selectedEventIds.set(new Set([event.id]));
      this.selectedEvents.set([event]);
    }
    this.#selectionAnchorId.set(event.id);
  }

  private rangeSelectFromAnchor(event: RosterContent): void {
    if (event.id === undefined) return;

    const anchorId = this.#selectionAnchorId();
    const eventMap = this.eventsResource.value();

    const selectSingle = () => {
      this.#selectedEventIds.set(new Set([event.id as string | number]));
      this.selectedEvents.set([event]);
      this.#selectionAnchorId.set(event.id as string | number);
    };

    if (!eventMap || anchorId === null || anchorId === event.id) {
      selectSingle();
      return;
    }

    const allEls = Array.from(
      document.querySelectorAll<HTMLElement>('.mbsc-timeline-event, .mbsc-schedule-event')
    );

    const idOf = (el: HTMLElement): string | number | null => {
      const raw = el.getAttribute('data-id');
      if (raw === null) return null;
      const n = Number(raw);
      return Number.isFinite(n) && String(n) === raw ? n : raw;
    };

    const anchorIdx = allEls.findIndex((el) => idOf(el) === anchorId);
    const targetIdx = allEls.findIndex((el) => idOf(el) === event.id);

    if (anchorIdx === -1 || targetIdx === -1) {
      selectSingle();
      return;
    }

    const [lo, hi] =
      anchorIdx < targetIdx ? [anchorIdx, targetIdx] : [targetIdx, anchorIdx];

    const rangeEvents: RosterContent[] = [];
    const rangeIds: (string | number)[] = [];
    for (let i = lo; i <= hi; i++) {
      const id = idOf(allEls[i]);
      if (id === null) continue;
      const ev = eventMap.get(id);
      if (!ev || rangeIds.includes(ev.id as string | number)) continue;
      rangeIds.push(ev.id as string | number);
      rangeEvents.push(ev);
    }

    if (!rangeIds.includes(anchorId) || !rangeIds.includes(event.id)) {
      selectSingle();
      return;
    }

    this.#selectedEventIds.set(new Set(rangeIds));
    this.selectedEvents.set(rangeEvents);
  }
  override async onEventRightClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    const event = args.event as RosterContent;
    if (event.id === undefined) return;
    if (event['external']) {
      args.domEvent.preventDefault();
      args.domEvent.stopPropagation();
      this.#externalEventRightClicked = true;
      return;
    }

    const selection = this.#selectedEventIds();
    const events = this.selectedEvents();

    if (!selection.has(event.id)) {
      const updatedSet = new Set(selection);
      updatedSet.add(event.id);

      this.#selectedEventIds.set(updatedSet);
      this.selectedEvents.set(
        events.some((e) => e.id === event.id) ? events : [...events, event]
      );
    }

    const mouseEvent = args.domEvent as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.contextMenuEventDate.set(null);
    this.contextMenuResource.set(null);
  }

  /** Outlook/Teams events are read-only; the calendar asks for the menu right after the right click. */
  #externalEventRightClicked = false;

  // No event menu when multi-selecting appointments (they share no action).
  override resolveEventMenu(): TemplateRef<any> | undefined {
    if (this.#externalEventRightClicked) {
      this.#externalEventRightClicked = false;
      return undefined;
    }
    const events = this.selectedEvents();
    const multiSelect = events.length > 1;
    const hasAppointment = events.some((e) => e?.additionalData?.type === 'APPOINTMENT');
    if (multiSelect && hasAppointment) return undefined;
    return this.eventMenu();
  }

  override async onEventDoubleClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    this.editEvent(args.event as RosterContent);
    return Promise.resolve();
  }

  private async openExternalEventDetails(event: RosterContent): Promise<void> {
    const details = (event['externalDetails'] ?? event.original?.['externalDetails']) as
      | EventDetails
      | undefined;
    if (!details) return;
    await this.modal({
      title: details.subject || this.t('#PMS_ROSTER.EXTERNAL_EVENT.TITLE')(),
      subtitle: this.t('#PMS_ROSTER.EXTERNAL_EVENT.TITLE')(),
      icon: 'fa-regular fa-arrow-up-right-from-square',
      separateContentSection: true,
      component: ExternalEventModalComponent,
      data: { details },
    });
  }

  override async onCellClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    this.#selectedEventIds.set(new Set());
    this.selectedEvents.set([]);
    this.#selectionAnchorId.set(null);
    this.#clickedCellDate.set(args.date ?? null);
    this.#clickedCellResource.set(args.resource ?? null);
    return Promise.resolve();
  }

  override async onCellRightClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    this.#selectedEventIds.set(new Set());
    this.selectedEvents.set([]);

    const mouseEvent = args.domEvent as MouseEvent;
    mouseEvent.preventDefault();
    mouseEvent.stopPropagation();

    this.contextMenuEventDate.set(args.date ?? null);
    this.contextMenuResource.set(args.resource ?? null);

    return Promise.resolve();
  }
  override async onCellDoubleClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    const date = args.date;
    const resource = args.resource;
    await this.createEvent([resource], date, 'Planeintrag', 3);
    return Promise.resolve();
  }
  override async onEventDragStart(args: MbscEventDragEvent, inst: any): Promise<void> {
    // TODO: Implement drag start logic
    return Promise.resolve();
  }
  override async onEventDragEnd(args: MbscEventDragEvent, inst: any): Promise<void> {
    if (args.event['external']) return;
    if (args.event['additionalData']) {
      const start = args.event.start as Date;
      const end = args.event.end as Date;
      const eventMap = this.eventsResource.value();
      if (!eventMap || !args.event.id) return;

      const toUpdateEvent = eventMap.get(args.event.id);
      if (!toUpdateEvent) return;

      // Convert start and end times to UTC
      const startUTC = new Date(
        Date.UTC(
          start.getFullYear(),
          start.getMonth(),
          start.getDate(),
          start.getHours(),
          start.getMinutes(),
          start.getSeconds()
        )
      );

      const endUTC = new Date(
        Date.UTC(
          end.getFullYear(),
          end.getMonth(),
          end.getDate(),
          end.getHours(),
          end.getMinutes(),
          end.getSeconds()
        )
      );

      const startString = startUTC.toISOString().replace('Z', '');
      const endString = endUTC.toISOString().replace('Z', '');

      eventMap.set(args.event.id, {
        ...toUpdateEvent,
        start: startString,
        end: endString,
        resource: args.resource ? [args.resource] : toUpdateEvent.resource,
        resize: true,
      });

      try {
        // Backend-only response fields must not be sent back
        const additionalData = { ...args.event['additionalData'] };
        delete additionalData.billable;
        delete additionalData.creatorUser;
        delete additionalData.createdAt;
        const result = await this.eventRepository.updateEvent({
          ...args.event,
          start: startString,
          end: endString,
          resource: args.resource ? [args.resource] : args.event.resource,
          additionalData: {
            ...additionalData,
            endtime: endString,
            begintime: startString,
            department: null,
            employees: [
              {
                employee: {
                  id: args.resource || args.event.resource?.[0],
                } as Employee,
                department: null,
              },
            ],
          },
        });

        if (!result || !result.id) return;
        this.updateEventsMap((map) =>
          map.set(result.id as string | number, {
            ...this.preserveReadonlyFields(args.event as RosterContent, result),
            resource: args.resource ? [args.resource] : result.resource,
            resize: true,
          })
        );
        this.messageBarService.success(`Eintrag wurde verschoben`);
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        this.refreshEvents();
      }
    }
  }

  /** Update payloads strip the backend-computed read-only fields (billable,
   *  creatorUser, createdAt, hours*Artefact) and the update response echoes
   *  them missing — carry them over from the pre-save event when patching the
   *  local events map, otherwise ticket hour displays go blank until the next
   *  full load. */
  private preserveReadonlyFields(
    previous: RosterContent,
    result: RosterContent
  ): RosterContent {
    const prevData = previous.additionalData;
    const resultData = result.additionalData;
    if (!prevData || !resultData) return result;
    const prevDetails = prevData.additionaldetail ?? [];
    return {
      ...result,
      additionalData: {
        ...resultData,
        billable: resultData.billable ?? prevData.billable,
        creatorUser: resultData.creatorUser ?? prevData.creatorUser,
        createdAt: resultData.createdAt ?? prevData.createdAt,
        additionaldetail: (resultData.additionaldetail ?? prevDetails).map(
          (detail, i) => ({
            ...detail,
            hoursPlannedArtefact:
              detail.hoursPlannedArtefact ?? prevDetails[i]?.hoursPlannedArtefact,
            hoursActualArtefact:
              detail.hoursActualArtefact ?? prevDetails[i]?.hoursActualArtefact,
          })
        ),
      },
    };
  }

  override onEventDelete(args: MbscEventDeleteEvent, inst: any): boolean {
    const event = args.event as RosterContent;
    if (!event) return false;
    let result = false;
    queueMicrotask(async () => {
      result = await this.deleteSelectedEvents();
    });
    return result;
  }

  override async deleteEvents(events: RosterContent[]): Promise<boolean> {
    if (events.length < 0 || !events) return Promise.resolve(false);
    const result = await this.askDelete({
      dismiss: true,
      question: this.t('#PMS_ROSTER.DELETE_CONFIRM')(),
    });
    if (!result || result === 'DISMISSED' || result === 'CANCEL' || result === 'NO')
      return false;
    try {
      await Promise.all(
        events
          .filter((event) => !!event.id)
          .map((event) => this.eventRepository.delete(Number(event.id)))
      );
      this.updateEventsMap((map) => {
        events.forEach((event) => {
          if (!event.id) return;
          map.delete(event.id);
        });
      });
      this.messageBarService.success(`Eintrag(e) wurden gelöscht.`);
      this.selectedEvents.set([]);
      this.#selectedEventIds.set(new Set());
      return true;
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      return false;
    }
  }
  override async deleteSelectedEvents(): Promise<boolean> {
    const selectedEvents = this.#selectedEventIds();
    const eventMap = this.eventsResource.value();
    if (selectedEvents.size < 1 || !eventMap) return Promise.resolve(false);
    return await this.deleteEvents(this.getEventsFromSelection(selectedEvents));
  }

  private getEventsFromSelection(eventIds: Set<string | number>): RosterContent[] {
    const eventMap = this.eventsResource.value();
    if (!eventMap) return [];
    return Array.from(eventIds)
      .filter((o) => o !== undefined)
      .map((eventId) => eventMap.get(eventId))
      .filter((o) => o !== undefined);
  }

  override onKeyDown(event: KeyboardEvent): Promise<void> {
    if (!(event.ctrlKey || event.metaKey)) return Promise.resolve();
    if (this.isFromInteractiveTarget(event)) return Promise.resolve();

    const key = event.key.toLowerCase();

    if (key === 'c' && this.selectedEvents().length) {
      event.preventDefault();
      this.handleContextMenuAction('copy');
      return Promise.resolve();
    }

    if (key === 'v') {
      const copiedEvents = this.copyEvents();
      const targetDate = this.#clickedCellDate();
      const targetResource = this.#clickedCellResource();

      if (!copiedEvents.length || !targetDate || targetResource === null) {
        return Promise.resolve();
      }

      event.preventDefault();
      for (const ev of copiedEvents) {
        this.duplicateEvent(ev, targetDate, targetResource);
      }
      this.#selectedEventIds.set(new Set());
      this.selectedEvents.set([]);
    }

    return Promise.resolve();
  }

  private isFromInteractiveTarget(event: KeyboardEvent): boolean {
    if (this.modalService.configs().length > 0) return true;
    const target = event.target as HTMLElement | null;
    if (!target) return false;
    if (
      target.closest(
        'input, textarea, select, [contenteditable=""], [contenteditable="true"]'
      )
    ) {
      return true;
    }
    return false;
  }

  //**** HELPER ****//
  override registerDepartmentHead(value: Signal<DepartmentHead | null>) {
    this.selectedDepartmentHead.set(value);
  }
  override registerBeginDate(value: Signal<string>) {
    this.beginDate.set(value);
  }

  override registerCalenderViewType(value: Signal<CalendarViewType>): void {
    this.calendarViewType.set(value);
  }

  override registerPlanMonthRange(from: Signal<Date>, to: Signal<Date>): void {
    this.planMonthFrom.set(from);
    this.planMonthTo.set(to);
  }

  override registerCalendarColumnWidth(value: Signal<MbscTimelineColumnWidth>): void {
    this.calendarColumnWidth.set(value ?? 'xlarge');
  }

  override registerCalendarDetails(value: Signal<'compact' | 'all'>): void {
    this.calendarDetails.set(value);
  }

  override registerEventTypesFilter(value: Signal<string[]>): void {
    this.selectedEventTypeFilter.set(value);
  }

  override registerResourceFilterString(value: Signal<string>): void {
    this.resourceFilterString.set(value);
  }

  override async loadEmployeeContent(structure: Structure, date: string): Promise<void> {
    let currentChunk: Employee[] = [];
    for (const depStructure of structure.departements) {
      for (const employee of depStructure.employees) {
        currentChunk.push(employee);
        if (currentChunk.length < 5) continue;
        await Promise.all(
          currentChunk.map((e) => this.loadDetail({ date: date, employeeId: e.id }))
        );
        currentChunk = [];
      }
    }
    if (currentChunk.length > 0) {
      await Promise.all(
        currentChunk.map((e) => this.loadDetail({ date: date, employeeId: e.id }))
      );
    }
  }

  private async loadDetail(params: LoadRosterDetailParams) {
    // check user permission before loading detail
    if (!this.isAuthorized('ROSTER_BALANCES')()) {
      return;
    }
    try {
      const detail = await this.resourceRepository.loadDetail({
        date: params.date,
        employeeId: params.employeeId,
      });
      this.map.set(params.employeeId, detail);
      this.employeeDetail.set(new Map(this.map));
    } catch (e) {
      handleRequestError(e, this.injector, { show: false });
    }
  }
  private toggleSelectedEvents(event: RosterContent) {
    if (!event.id) return;
    const selection = this.#selectedEventIds();
    const events = this.selectedEvents();
    if (selection.has(event.id)) {
      selection.delete(event.id);
      this.#selectedEventIds.set(new Set(selection));
      const updatedEvents = events.filter((e) => e.id !== event.id);
      this.selectedEvents.set(updatedEvents);
    } else {
      const updatedSet = selection.add(event.id);
      this.#selectedEventIds.set(new Set(updatedSet));
      this.selectedEvents.set([...events, event]);
    }
  }

  private async createEvent(
    resource: (number | string)[],
    date: Date,
    eventType?: string,
    navTabIndex?: number
  ): Promise<void> {
    const eventData = await this.modal({
      title: this.t('#PMS_ROSTER.CREATE_ENTRY_TITLE')(),
      subtitle: this.t('#PMS_ROSTER.DETAILS_SUBTITLE')(),
      icon: 'fa-regular fa-calendar-day',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      component: EventFormComponent,
      data: {
        eventType: eventType ?? 'Planeintrag',
        date: toIsoDate(date),
        resource: resource,
        resources: this.allEmployeesFromStructure(),
        navTabIndex: navTabIndex ?? 0,
      },
    });
    if (eventData === undefined || eventData === null || eventData === 'DISMISSED')
      return;
    if (eventData.eventType !== 'Planeintrag') return; //TODO: handle appointment creation

    const structure = this.structureResource.value();
    let department: Department | null = null;
    structure?.departements.forEach((d) => {
      if (d.employees.findIndex((emp) => emp.id === Number(eventData.employeeId)) >= 0) {
        department = d.department;
      }
    });

    const additinaldetail = [
      {
        tabname: eventData.ticketId ? 'TICKET' : eventData.objectId ? 'OBJECT' : null,
        tabid: eventData.ticketId
          ? eventData.ticketId
          : eventData.objectId
            ? eventData.objectId
            : null,
        name: null,
        title: null,
        comment: null,
        state: eventData.ticketId || eventData.objectId ? 'UNDEFINED' : null,
        key: null,
        id: null,
      },
    ];

    const startString = eventData.beginDay + 'T' + eventData.beginTime;
    const endString = eventData.endDay + 'T' + eventData.endTime;
    const payload: Partial<RosterContent> = [
      {
        resource: [Number(eventData.employeeId)],
        date: startString,
        allDay: false,
        start: startString,
        end: endString,
        title: '',
        text: '',
        additionalData: {
          comment: eventData.comment,
          state: 'UNDEFINED',
          pauseHours: '00:30:00',
          plannedHours: '23:59:59',
          targetHours: '23:59:59',
          department: department,
          employees: [
            {
              employee: {
                id: Number(eventData.employeeId),
              } as Employee,
              department: department,
            },
          ],
          begintime: startString,
          endtime: endString,
          type: 'PLAN',
          additionaldetail:
            eventData.ticketId || eventData.objectId ? additinaldetail : [],
        },
      },
    ] as unknown as RosterContent;
    try {
      const result = await this.eventRepository.createEvent(payload);
      if (!result.id) return;
      this.updateEventsMap((map) =>
        map.set(result.id as string | number, { ...result, resize: true })
      );
      this.messageBarService.success(
        eventData.eventType + ` ${this.t('#PMS_ROSTER.WAS_CREATED')()}`
      );
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  private async editEvent(event: RosterContent): Promise<void> {
    if (event['external'] || event.original?.['external']) {
      await this.openExternalEventDetails(event);
      return;
    }
    const eventData = await this.modal({
      title: this.t('#PMS_ROSTER.EDIT_ENTRY_TITLE')(),
      subtitle: this.t('#PMS_ROSTER.EDIT_ENTRY_SUBTITLE', {
        creatorUser: event.additionalData?.creatorUser ?? '',
        createdAt: event.additionalData?.createdAt ?? '',
      })(),
      icon: 'fa-regular fa-calendar-day',
      separateContentSection: true,
      surfaceControls: { minimize: true, dock: true },
      component: EventFormComponent,
      data: {
        eventType:
          event.additionalData.additionaldetail?.[0]?.tabname === 'TICKET' ||
          event.additionalData.additionaldetail?.[0]?.tabname === 'OBJECT' ||
          event.additionalData.type === 'PLAN'
            ? 'Planeintrag'
            : 'Termin',
        event: event,
        resources: this.allEmployeesFromStructure(),
        resource: event.resource as (number | string)[] | undefined,
        editMode: true,
        navTabIndex: 3,
      },
    });
    if (eventData === undefined || eventData === null || eventData === 'DISMISSED')
      return;
    if (eventData.eventType !== 'Planeintrag') return; //TODO: handle appointment creation

    const resolvedEmployeeId =
      eventData.employeeId ?? event.additionalData.employees[0].employee.id;
    const editStructure = this.structureResource.value();
    let editDepartment: Department | null = event.additionalData.employees[0].department;
    editStructure?.departements.forEach((d) => {
      if (d.employees.findIndex((emp) => emp.id === Number(resolvedEmployeeId)) >= 0) {
        editDepartment = d.department;
      }
    });

    const startString = eventData.beginDay.split('T')[0] + 'T' + eventData.beginTime;
    const endString = eventData.beginDay.split('T')[0] + 'T' + eventData.endTime;
    const additionalData = { ...event.additionalData };
    delete additionalData.billable;
    delete additionalData.creatorUser;
    delete additionalData.createdAt;
    const existingDetail = additionalData.additionaldetail?.[0];
    const body = {
      ...event,
      start: startString,
      end: endString,
      additionalData: {
        ...additionalData,
        state: eventData.state,
        comment: eventData.comment,
        endtime: endString,
        department: editDepartment,
        employees: [
          {
            employee: {
              ...additionalData.employees[0].employee,
              id: resolvedEmployeeId,
            },
            department: editDepartment,
          },
        ],
        additionaldetail:
          eventData.ticketId || eventData.objectId
            ? [
                {
                  comment: existingDetail?.comment ?? null,
                  tabname: eventData.ticketId
                    ? 'TICKET'
                    : eventData.objectId
                      ? 'OBJECT'
                      : null,
                  tabid: eventData.ticketId ? eventData.ticketId : eventData.objectId,
                  key: existingDetail?.key ?? null,
                  id: existingDetail?.id ?? null,
                  name: existingDetail?.name ?? null,
                  title: existingDetail?.title ?? null,
                  state: existingDetail?.state ?? 'UNDEFINED',
                  hoursPlannedArtefact: existingDetail?.hoursPlannedArtefact,
                  hoursActualArtefact: existingDetail?.hoursActualArtefact,
                },
              ]
            : [],
        begintime: startString,
      },
    } as RosterContent;
    try {
      const result = await this.eventRepository.updateEvent(body);
      if (!result || !result.id) return;
      this.updateEventsMap((map) =>
        map.set(result.id as string | number, {
          ...this.preserveReadonlyFields(event, result),
          resize: true,
        })
      );
      this.messageBarService.success(`Eintrag wurde erfolgreich bearbeitet.`);
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
      this.refreshEvents();
    }
  }
  private async duplicateEvent(
    event: any,
    date?: Date,
    targetResource?: number | string
  ): Promise<void> {
    try {
      //make date timezone less
      const newDate = date
        ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        : undefined;

      const newStartDate = newDate
        ? toLocalDateString(newDate) + 'T' + event.start.split('T')[1]
        : event.start;
      const newEndDate = newDate
        ? toLocalDateString(newDate) + 'T' + event.end.split('T')[1]
        : event.end;
      // Backend-only response fields must not be sent back
      const additionalData = { ...event.additionalData };
      delete additionalData.billable;
      delete additionalData.creatorUser;
      delete additionalData.createdAt;
      const existingDetail = additionalData.additionaldetail?.[0];
      const newEvent = [
        {
          ...event,
          id: null,
          date: newDate?.toISOString() ?? event.date,
          start: newDate ? newStartDate : event.start,
          end: newDate ? newEndDate : event.end,
          resource: targetResource ? [targetResource] : event.resource,
          additionalData: {
            ...additionalData,
            id: null,
            begintime: newDate ? newStartDate : additionalData.begintime,
            endtime: newDate ? newEndDate : additionalData.endtime,
            state: 'UNDEFINED',
            department: null,
            employees: additionalData.employees.map((emp: any) => ({
              ...emp,
              employee: {
                ...emp.employee,
                id: targetResource ? Number(targetResource) : emp.employee.id,
              },
              department: null,
            })),
            additionaldetail: existingDetail
              ? [
                  {
                    tabname: existingDetail.tabname,
                    tabid: existingDetail.tabid,
                    title: existingDetail.title,
                    comment: existingDetail.comment,
                    id: null,
                    key: null,
                    name: null,
                    state: 'UNDEFINED',
                  },
                ]
              : [],
          },
        },
      ] as unknown as RosterContent;
      const result = await this.eventRepository.createEvent(newEvent as any);
      if (!result.id) return;
      this.updateEventsMap((map) =>
        map.set(result.id as string | number, { ...result, resize: true })
      );
      this.pendingLocalCreates.set(result.id as string | number, Date.now());
      this.messageBarService.success(
        event.original?.['additionalData']
          ? 'Planeintrag'
          : 'Termin' + ` wurde dupliziert.`
      );
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }
  }

  override readonly copyEvents = signal<RosterContent[]>([]);
  readonly contextMenuEventDate = signal<Date | null>(null);
  readonly contextMenuResource = signal<number | string | null>(null);
  readonly #clickedCellDate = signal<Date | null>(null);
  readonly #clickedCellResource = signal<number | string | null>(null);

  override handleContextMenuAction(action: string): void {
    const selectedEvents = this.selectedEvents();

    switch (action) {
      case 'duplicate':
        if (!selectedEvents.length) return;
        selectedEvents.forEach((event) => this.duplicateEvent(event));
        this.#selectedEventIds.set(new Set());
        this.selectedEvents.set([]);
        break;

      case 'edit':
        if (!selectedEvents.length) return;
        this.editEvent(selectedEvents[0]);
        break;

      case 'delete':
        if (!selectedEvents.length) return;
        this.deleteSelectedEvents();
        break;

      case 'copy':
        if (!selectedEvents.length) return;
        this.copyEvents.set(selectedEvents);
        break;

      case 'paste': {
        const copiedEvents = this.copyEvents();
        const targetDate = this.contextMenuEventDate();
        const targetResource = this.contextMenuResource();

        if (!copiedEvents.length || !targetDate || !targetResource) return;
        for (const event of copiedEvents) {
          this.duplicateEvent(event, targetDate as Date, targetResource);
        }
        this.#selectedEventIds.set(new Set());
        this.selectedEvents.set([]);
        break;
      }

      case 'create': {
        this.createEvent(
          this.contextMenuResource()
            ? [this.contextMenuResource() as number | string]
            : [],
          this.contextMenuEventDate() as Date
        );
        break;
      }

      case 'createEmpty': {
        this.createEvent([], new Date());
        break;
      }

      case 'createPlanEntry': {
        this.createEvent(
          this.contextMenuResource()
            ? [this.contextMenuResource() as number | string]
            : [],
          new Date(),
          'Planeintrag'
        );
        break;
      }

      case 'createAppointment': {
        this.createEvent(
          this.contextMenuResource()
            ? [this.contextMenuResource() as number | string]
            : [],
          new Date(),
          'Termin'
        );
        break;
      }

      case 'openTicket': {
        const selectedEvents = this.selectedEvents();
        this.ticketService.navigateToTicket(
          selectedEvents[0].additionalData.additionaldetail?.[0]?.tabid
        );
        break;
      }

      case 'copyTicketKey': {
        const selectedEvents = this.selectedEvents();
        const ticketKey =
          selectedEvents[0].additionalData.additionaldetail?.[0]?.tabid ?? '';
        navigator.clipboard.writeText(ticketKey.toString());
        this.messageBarService.success(
          'Ticket-Nummer wurde in die Zwischenablage kopiert.'
        );
        break;
      }
      case 'copyTicketName': {
        const selectedEvents = this.selectedEvents();
        const title = selectedEvents[0].title ?? '';
        const ticketName = title.replace(/^(Objekt:\s*\d+\s*-\s*|T\d+\s*-\s*)/, '');
        navigator.clipboard.writeText(ticketName);
        this.messageBarService.success(
          'Ticket-Name wurde in die Zwischenablage kopiert.'
        );
        break;
      }
      case 'copyTicketKeyAndName': {
        const selectedEvents = this.selectedEvents();
        const ticketKey =
          selectedEvents[0].additionalData.additionaldetail?.[0]?.tabid ?? '';
        const title = selectedEvents[0].title ?? '';
        const ticketName = title.replace(/^(Objekt:\s*\d+\s*-\s*|T\d+\s*-\s*)/, '');
        navigator.clipboard.writeText(`${ticketKey} - ${ticketName}`);
        this.messageBarService.success(
          'Ticket-Nr. und Name wurden in die Zwischenablage kopiert.'
        );
        break;
      }

      case 'copyObjectKey': {
        const selectedEvents = this.selectedEvents();
        const key = selectedEvents[0].additionalData.additionaldetail?.[0]?.key ?? '';
        navigator.clipboard.writeText(key.toString());
        this.messageBarService.success(
          'Objekt-Nummer wurde in die Zwischenablage kopiert.'
        );
        break;
      }

      case 'changeStatus:UNDEFINED':
      case 'changeStatus:DONE':
      case 'changeStatus:HANDOVER_TO_OTHERS': {
        if (!selectedEvents.length) return;
        const status = action.split(':')[1];
        void this.changeEventStatus(selectedEvents, status);
        break;
      }

      case 'copyToNextMonday': {
        if (!selectedEvents.length) return;
        const today = new Date();
        const dayOfWeek = today.getDay();
        const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
        const nextMonday = new Date(today);
        nextMonday.setDate(today.getDate() + daysUntilMonday);
        for (const event of selectedEvents) {
          this.duplicateEvent(event, nextMonday);
        }
        this.#selectedEventIds.set(new Set());
        this.selectedEvents.set([]);
        break;
      }

      default:
        console.warn('Unknown context menu action:', action);
    }

    this.closeContextMenu();
  }

  private async changeEventStatus(
    events: RosterContent[],
    status: string
  ): Promise<void> {
    for (const event of events) {
      const additionalData = { ...event.additionalData };
      delete additionalData.billable;
      delete additionalData.creatorUser;
      delete additionalData.createdAt;
      const body = {
        ...event,
        additionalData: {
          ...additionalData,
          state: status,
        },
      } as RosterContent;
      try {
        const result = await this.eventRepository.updateEvent(body);
        if (!result || !result.id) continue;
        this.updateEventsMap((map) =>
          map.set(result.id as string | number, {
            ...this.preserveReadonlyFields(event, result),
            resize: true,
          })
        );
      } catch (e) {
        handleRequestError(e, this.injector, { show: true });
        this.refreshEvents();
        return;
      }
    }
    this.messageBarService.success(this.t('#PMS_ROSTER.STATUS_CHANGED_SUCCESS')());
  }

  private closeContextMenu(): void {
    this.closeContextMenuOverlay?.();
    this.selectedEvents.set([]);
    this.#selectedEventIds.set(new Set());
    this.contextMenuEventDate.set(null);
  }
}
